import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Skeleton,
  useTheme,
  Fade,
} from '@mui/material';
import {
  AutoAwesome as SparklesIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingIcon,
  ShowChart as ChartIcon,
  ArrowForward as ArrowForwardIcon,
  PushPin as PinIcon,
  Share as ShareIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { AiInsightConfig } from '../types/analytics.types';
import { useDashboardAiContext } from '../contexts/DashboardAiContext';

export interface AiInsightCardProps {
  config: AiInsightConfig;
  title?: string;
  dashboardFilters?: Record<string, any>;
  dashboardMetadata?: {
    name?: string;
    description?: string;
    dateRange?: { start: Date | null; end: Date | null };
  };
  isLiveMode?: boolean;
  className?: string;
  // Dashboard identifiers for caching
  dashboardKey?: string;
  dashboardId?: string;
  insightCardId?: string;
}

/**
 * AI Insight Card Component
 * 
 * Displays AI-generated insights in dashboards with support for:
 * - 3 size variants: compact, standard, featured
 * - Mock data for demo mode
 * - Live AI agent calls with markdown response rendering
 * - "Learn Why" expansion to full chat interface with conversation context
 */
export const AiInsightCard: React.FC<AiInsightCardProps> = ({
  config,
  title,
  dashboardFilters = {},
  dashboardMetadata = {},
  isLiveMode = false,
  className,
  dashboardKey,
  dashboardId,
  insightCardId,
}) => {
  const theme = useTheme();
  const dashboardAi = useDashboardAiContext();
  
  // Debug: Log on mount and when lastInsightResponse changes
  useEffect(() => {
    console.log('🎯 [AiInsightCard] Component mounted/context available:', {
      hasDashboardAi: !!dashboardAi,
      hasLastInsightResponse: !!dashboardAi?.lastInsightResponse,
      isLiveMode,
      hasExecuteInsightPrompt: !!dashboardAi?.executeInsightPrompt,
      isReady: dashboardAi?.isReady,
      applicationKey: dashboardAi?.applicationKey,
      assistantId: dashboardAi?.assistantId,
    });
    // Only log when these specific values change, not the whole dashboardAi object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardAi?.applicationKey, dashboardAi?.isReady, dashboardAi?.lastInsightResponse?.messageId, isLiveMode]);
  
  
  // Validate and provide defaults for config to prevent React hooks errors
  const safeConfig = useMemo(() => {
    if (!config || typeof config !== 'object') {
      return {
        variant: 'compact' as const,
        prompt: '',
        icon: 'sparkles' as const,
        showLearnMore: true,
        includeFilters: true,
        includeDashboardMetadata: true,
        responseFormat: undefined,
      };
    }
    
    // Debug: Log backgroundColor to help diagnose config loading issues
    if (process.env.NODE_ENV === 'development' && config.backgroundColor) {
      console.log(`[AiInsightCard] backgroundColor from config:`, config.backgroundColor);
    }
    
    return {
      variant: config.variant || 'compact',
      prompt: config.prompt || '',
      icon: config.icon || 'sparkles',
      accentColor: config.accentColor,
      backgroundColor: config.backgroundColor,
      showLearnMore: config.showLearnMore !== false,
      learnMoreText: config.learnMoreText,
      includeFilters: config.includeFilters !== false,
      includeDashboardMetadata: config.includeDashboardMetadata !== false,
      refreshOnFilterChange: config.refreshOnFilterChange !== false,
      mockData: config.mockData,
      responseFormat: config.responseFormat,
    };
  }, [config]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insightData, setInsightData] = useState<{
    headline?: string;
    byline?: string;
    fullText?: string;
    ctaText?: string;
    markdown?: string;
    threadId?: string;
    messageId?: string;
  } | null>(null);
  
  // Track the last messageId we've processed to avoid duplicate updates
  const lastProcessedMessageIdRef = useRef<string | null>(null);
  
  // Watch for changes to lastInsightResponse and update insightData immediately
  useEffect(() => {
    const response = dashboardAi?.lastInsightResponse;
    
    if (!response) {
      return;
    }
    
    // Extract headline and byline
    const headline = response.entityCards?.['headline:headline'] || response.intelligenceCard?.headline;
    const byline = response.entityCards?.['byline:byline'] || response.intelligenceCard?.byline;
    
    console.log('🔔 [AiInsightCard] Context changed - lastInsightResponse:', {
      hasEntityCards: !!response.entityCards,
      entityCardsKeys: response.entityCards ? Object.keys(response.entityCards) : [],
      hasIntelligenceCard: !!response.intelligenceCard,
      headline,
      byline: byline ? byline.substring(0, 50) + '...' : undefined,
      messageId: response.messageId,
      currentInsightDataMessageId: insightData?.messageId,
    });
    
    // Only update if we have headline or byline and it's different from what we currently have
    if (headline || byline) {
      const parsed = extractInsightFromResponse(response);
      const currentHeadline = insightData?.headline;
      const currentByline = insightData?.byline;
      const isDifferent = 
        parsed.headline !== currentHeadline || 
        parsed.byline !== currentByline ||
        insightData?.messageId !== response.messageId;
      
      if (isDifferent && (parsed.headline || parsed.byline)) {
        console.log('✅ [AiInsightCard] Updating insightData from lastInsightResponse:', {
          headline: parsed.headline,
          byline: parsed.byline ? parsed.byline.substring(0, 50) + '...' : undefined,
          messageId: response.messageId,
          reason: insightData?.messageId !== response.messageId ? 'new messageId' : 'data changed',
        });
        
        setInsightData({
          ...parsed,
          markdown: response.markdown || response.text,
          threadId: response.threadId,
          messageId: response.messageId,
        });
      } else {
        console.log('ℹ️ [AiInsightCard] Skipping update - data unchanged');
      }
    }
  }, [
    dashboardAi?.lastInsightResponse?.messageId,
    dashboardAi?.lastInsightResponse?.entityCards,
    dashboardAi?.lastInsightResponse?.intelligenceCard,
  ]);
  
  // Animation state for headline/byline cycling
  const [showHeadline, setShowHeadline] = useState(true);
  
  // Get the appropriate icon based on config
  const InsightIcon = useMemo(() => {
    switch (safeConfig.icon) {
      case 'sparkles':
        return SparklesIcon;
      case 'lightbulb':
        return LightbulbIcon;
      case 'trending':
        return TrendingIcon;
      case 'chart':
        return ChartIcon;
      default:
        return SparklesIcon;
    }
  }, [safeConfig.icon]);

  // Build the full prompt with context
  const buildPromptWithContext = useMemo(() => {
    if (!safeConfig.prompt) {
      return '';
    }
    
    let fullPrompt = safeConfig.prompt;
    
    // Add dashboard metadata context if enabled
    if (safeConfig.includeDashboardMetadata && dashboardMetadata) {
      const metadataContext: string[] = [];
      
      if (dashboardMetadata.name) {
        metadataContext.push(`Dashboard: ${dashboardMetadata.name}`);
      }
      
      if (dashboardMetadata.dateRange?.start && dashboardMetadata.dateRange?.end) {
        const startStr = dashboardMetadata.dateRange.start.toLocaleDateString();
        const endStr = dashboardMetadata.dateRange.end.toLocaleDateString();
        metadataContext.push(`Date Range: ${startStr} to ${endStr}`);
      }
      
      if (metadataContext.length > 0) {
        fullPrompt = `Context:\n${metadataContext.join('\n')}\n\n${fullPrompt}`;
      }
    }
    
    // Add filter context if enabled
    if (safeConfig.includeFilters && Object.keys(dashboardFilters).length > 0) {
      const filterContext = Object.entries(dashboardFilters)
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join('\n');
      
      if (filterContext) {
        fullPrompt = `${fullPrompt}\n\nActive Filters:\n${filterContext}`;
      }
    }
    
    // Add format hints for Intelligence Cards (but don't force JSON - keep markdown for other functions)
    // The backend will parse markdown and extract headline/byline into metadata
    const responseFormat = safeConfig.responseFormat;
    const needsStructuredOutput = responseFormat?.headline !== false; // Default to true unless explicitly disabled
    
    if (needsStructuredOutput) {
      fullPrompt = `${fullPrompt}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RESPONSE FORMAT HINT - INTELLIGENCE CARD
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
For optimal display on an Intelligence Card, structure your response as markdown:

## Your Headline Here

Your byline or key insight (1-2 sentences) that provides context and expands on the headline.

[Additional detailed explanation if needed]

IMPORTANT:
- Use ## for the headline (will be displayed prominently)
- Follow with a brief byline paragraph (1-2 sentences)
- Both support markdown formatting (bold with **text**, italic with *text*)
- Keep headline concise (max 80 characters) and byline brief (max 200 characters)`;
    }
    
    return fullPrompt;
  }, [safeConfig.prompt, safeConfig.includeDashboardMetadata, safeConfig.includeFilters, safeConfig.responseFormat, dashboardFilters, dashboardMetadata]);

  // Helper to extract headline/byline from an AI response
  const extractInsightFromResponse = (response: { 
    entityCards?: Record<string, string>; 
    intelligenceCard?: { headline?: string; byline?: string; fullText?: string };
    markdown?: string;
    text?: string;
    threadId?: string;
    messageId?: string;
  }) => {
    let parsed: { headline?: string; byline?: string; fullText?: string } = {};
    
    // First, check entityCards for headline/byline (token-based approach with unified ${type:id} format)
    if (response.entityCards) {
      console.log('🔍 [AiInsightCard] Checking entityCards for headline/byline');
      console.log(`   EntityCards keys: ${Object.keys(response.entityCards).join(', ')}`);
      console.log(`   Has headline:headline: ${!!response.entityCards['headline:headline']}`);
      console.log(`   Has byline:byline: ${!!response.entityCards['byline:byline']}`);
      
      const headlineValue = response.entityCards['headline:headline'] || response.entityCards.headline;
      const bylineValue = response.entityCards['byline:byline'] || response.entityCards.byline;
      
      if (headlineValue) {
        parsed.headline = headlineValue;
        console.log('✅ [AiInsightCard] Found headline in entityCards:', parsed.headline);
      } else {
        console.warn('⚠️ [AiInsightCard] No headline found in entityCards');
      }
      if (bylineValue) {
        parsed.byline = bylineValue;
        console.log('✅ [AiInsightCard] Found byline in entityCards:', parsed.byline.substring(0, 100));
      } else {
        console.warn('⚠️ [AiInsightCard] No byline found in entityCards');
      }
    } else {
      console.warn('⚠️ [AiInsightCard] No entityCards in response');
    }
    
    // Fallback: Use fields from intelligenceCard (backend extracted from markdown)
    if (response.intelligenceCard) {
      if (!parsed.headline && response.intelligenceCard.headline) {
        parsed.headline = response.intelligenceCard.headline;
        console.log('✅ [AiInsightCard] Using headline from intelligenceCard:', parsed.headline);
      }
      if (!parsed.byline && response.intelligenceCard.byline) {
        parsed.byline = response.intelligenceCard.byline;
        console.log('✅ [AiInsightCard] Using byline from intelligenceCard:', parsed.byline.substring(0, 100));
      }
      if (response.intelligenceCard.fullText && !parsed.fullText) {
        parsed.fullText = response.intelligenceCard.fullText;
      }
    }
    
    // Last resort: Parse markdown response to extract headline, byline, etc.
    if (!parsed.headline || !parsed.byline) {
      const markdownParsed = parseMarkdownResponse(response.markdown || response.text || '');
      if (!parsed.headline && markdownParsed.headline) {
        parsed.headline = markdownParsed.headline;
        console.log('✅ [AiInsightCard] Extracted headline from markdown:', parsed.headline);
      }
      if (!parsed.byline && markdownParsed.byline) {
        parsed.byline = markdownParsed.byline;
        console.log('✅ [AiInsightCard] Extracted byline from markdown:', parsed.byline.substring(0, 100));
      }
      if (markdownParsed.fullText && !parsed.fullText) {
        parsed.fullText = markdownParsed.fullText;
      }
    }
    
    return parsed;
  };
  
  // Track if we've already triggered the prompt for this card instance
  const hasTriggeredPromptRef = useRef(false);
  
  // Load insight data - check context first, then call AI (only in live mode), then mock data
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;
    
    const loadInsight = async () => {
      console.log('🔄 [AiInsightCard] loadInsight called:', {
        isLiveMode,
        hasMockData: !!safeConfig.mockData,
        hasDashboardAi: !!dashboardAi,
        hasLastInsightResponse: !!dashboardAi?.lastInsightResponse,
        hasExecuteInsightPrompt: !!dashboardAi?.executeInsightPrompt,
        prompt: safeConfig.prompt?.substring(0, 50) + '...',
        lastInsightResponseMessageId: dashboardAi?.lastInsightResponse?.messageId || 'none',
        hasTriggeredPrompt: hasTriggeredPromptRef.current,
      });
      
      // In live mode, always try to call AI if we haven't triggered yet or if there's no response
      if (isLiveMode && safeConfig.prompt) {
        // Check if dashboard AI context is available
        if (!dashboardAi) {
          console.warn('⚠️ [AiInsightCard] Dashboard AI context not available - DashboardAiProvider may not be wrapping the component');
          console.warn('⚠️ [AiInsightCard] This usually means applicationKey is not available from DerivedApplicationContext');
          // Don't set error immediately - wait a bit for context to initialize
          // The context might be loading asynchronously
          if (!hasTriggeredPromptRef.current) {
            console.log('⏳ [AiInsightCard] Waiting for DashboardAiContext to become available...');
            // Set loading state to show thinking animation
            setLoading(true);
            // Try again after a short delay
            timeoutId = setTimeout(() => {
              // Check if component is still mounted
              if (isMounted) {
                setError('AI service not available - DashboardAiContext not initialized. Make sure DashboardAiProvider wraps the dashboard.');
                setLoading(false);
              }
            }, 2000);
          }
          return;
        }
        
        // Check if executeInsightPrompt function exists
        if (!dashboardAi.executeInsightPrompt) {
          console.warn('⚠️ [AiInsightCard] executeInsightPrompt not available');
          setError('AI service not available - executeInsightPrompt missing');
          setLoading(false);
          return;
        }
        
        // Check if assistant is ready (initialized)
        if (!dashboardAi.isReady) {
          console.log('⏳ [AiInsightCard] Assistant not ready yet, initializing...');
          setLoading(true);
          setError(null);
          
          try {
            // Try to initialize the assistant
            await dashboardAi.initializeAssistant();
            console.log('✅ [AiInsightCard] Assistant initialized');
          } catch (initErr) {
            console.error('❌ [AiInsightCard] Failed to initialize assistant:', initErr);
            setError('Failed to initialize AI assistant');
            setLoading(false);
            return;
          }
        }
        
        // Check if we have a valid response for this specific card (matching insightCardId)
        const hasValidResponse = dashboardAi?.lastInsightResponse && 
          dashboardAi.lastInsightResponse.messageId &&
          (dashboardAi.lastInsightResponse.entityCards?.['headline:headline'] || 
           dashboardAi.lastInsightResponse.intelligenceCard?.headline);
        
        // Only trigger if we haven't already triggered OR if we don't have a valid response
        if (!hasTriggeredPromptRef.current || !hasValidResponse) {
          console.log('🚀 [AiInsightCard] Calling AI agent (live mode) - auto-trigger');
          setLoading(true);
          setError(null);
          hasTriggeredPromptRef.current = true;
          
          try {
            const response = await dashboardAi.executeInsightPrompt(
              buildPromptWithContext,
              (status) => {
                // Update loading state based on status
                console.log(`🔄 [AiInsightCard] Status update: ${status}`);
                if (status === 'Complete' || status === 'Error') {
                  setLoading(false);
                }
              },
              {
                dashboardKey,
                dashboardId,
                insightCardId,
              }
            );
            
            console.log('📊 [AiInsightCard] Processing response:', {
              hasEntityCards: !!response.entityCards,
              hasIntelligenceCard: !!response.intelligenceCard,
              hasMarkdown: !!response.markdown,
              hasText: !!response.text,
              entityCardsKeys: response.entityCards ? Object.keys(response.entityCards) : [],
              intelligenceCard: response.intelligenceCard,
            });
            
            const parsed = extractInsightFromResponse(response);
            
            console.log('📊 [AiInsightCard] Final parsed fields:', {
              headline: parsed.headline,
              byline: parsed.byline,
              hasFullText: !!parsed.fullText,
            });
            
            if (parsed.headline || parsed.byline) {
              const insightDataToSet = {
                ...parsed,
                markdown: response.markdown || response.text,
                threadId: response.threadId,
                messageId: response.messageId,
              };
              
              console.log('📊 [AiInsightCard] Setting insightData from AI:', {
                headline: insightDataToSet.headline,
                byline: insightDataToSet.byline,
              });
              
              setInsightData(insightDataToSet);
              setLoading(false);
              return;
            } else {
              console.warn('⚠️ [AiInsightCard] AI response had no headline/byline');
              setLoading(false);
            }
          } catch (err) {
            console.error('❌ [AiInsightCard] Failed to load insight from AI:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate insight');
            setLoading(false);
          }
        } else {
          // We have a valid response, use it
          console.log('✅ [AiInsightCard] Using existing response from context');
          const parsed = extractInsightFromResponse(dashboardAi.lastInsightResponse);
          
          if (parsed.headline || parsed.byline) {
            setInsightData({
              ...parsed,
              markdown: dashboardAi.lastInsightResponse.markdown || dashboardAi.lastInsightResponse.text,
              threadId: dashboardAi.lastInsightResponse.threadId,
              messageId: dashboardAi.lastInsightResponse.messageId,
            });
            setLoading(false);
            return;
          }
        }
      }
      
      // PRIORITY: Check if there's already AI data in the context (from previous call or Test Prompt)
      if (dashboardAi?.lastInsightResponse && !isLiveMode) {
        console.log('📊 [AiInsightCard] Found lastInsightResponse in context (non-live mode)');
        const parsed = extractInsightFromResponse(dashboardAi.lastInsightResponse);
        
        if (parsed.headline || parsed.byline) {
          setInsightData({
            ...parsed,
            markdown: dashboardAi.lastInsightResponse.markdown || dashboardAi.lastInsightResponse.text,
            threadId: dashboardAi.lastInsightResponse.threadId,
            messageId: dashboardAi.lastInsightResponse.messageId,
          });
          return;
        }
      }
      
      // PRIORITY: In mock mode, use mock data
      if (!isLiveMode && safeConfig.mockData) {
        console.log('📋 [AiInsightCard] Using MOCK data (mock mode)');
        setInsightData({
          headline: safeConfig.mockData.headline,
          byline: safeConfig.mockData.byline,
          fullText: safeConfig.mockData.fullText,
          ctaText: safeConfig.mockData.ctaText,
        });
        return;
      }
      
      // If we're in live mode but don't have a prompt, show error
      if (isLiveMode && !safeConfig.prompt) {
        setError('No prompt configured for this insight card');
        setLoading(false);
        return;
      }
      
      console.log('ℹ️ [AiInsightCard] Waiting for AI response...');
    };
    
    loadInsight();
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // Only re-run when these specific values change, not when the objects themselves change
    // We intentionally exclude buildPromptWithContext and dashboardAi object to prevent loops
    // Instead, we track the specific properties that matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLiveMode, 
    safeConfig.mockData, 
    safeConfig.prompt,
    dashboardAi?.applicationKey, // Use specific properties instead of whole object
    dashboardAi?.isReady,
    dashboardAi?.lastInsightResponse?.messageId, // Only re-run when messageId changes
    dashboardKey, 
    dashboardId, 
    insightCardId,
    // Track filter/metadata changes via their keys/values, not the objects themselves
    // This prevents re-runs when objects are recreated with same values
    safeConfig.includeFilters,
    safeConfig.includeDashboardMetadata,
    dashboardMetadata?.name,
    dashboardMetadata?.dateRange?.start?.getTime(),
    dashboardMetadata?.dateRange?.end?.getTime(),
  ]);

  // Cycle between headline and byline with fade animation
  useEffect(() => {
    const displayData = insightData || safeConfig.mockData;
    const hasBoth = displayData?.headline && displayData?.byline;
    
    if (!hasBoth) {
      setShowHeadline(true);
      return;
    }
    
    // Start with headline
    setShowHeadline(true);
    
    // Cycle every 4 seconds: headline -> byline -> headline -> ...
    const cycleInterval = setInterval(() => {
      setShowHeadline(prev => !prev);
    }, 4000);
    
    return () => {
      clearInterval(cycleInterval);
    };
  }, [insightData, safeConfig.mockData]);

  // Parse response to extract structured content (supports both JSON and markdown formats)
  const parseMarkdownResponse = (responseText: string): { headline?: string; byline?: string; fullText?: string } => {
    let headline: string | undefined;
    let byline: string | undefined;
    let fullText: string | undefined;
    
    // First, try to parse as JSON (structured output from AI)
    try {
      // Try to extract JSON from the response (might be wrapped in markdown code blocks or plain JSON)
      let jsonText = responseText.trim();
      
      // Remove markdown code block markers if present
      jsonText = jsonText.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/\s*```$/, '');
      
      // Try to find JSON object in the text
      const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        
        // Extract structured fields from JSON response
        if (parsed.headline) {
          headline = parsed.headline.trim();
        }
        if (parsed.byline) {
          byline = parsed.byline.trim();
        }
        if (parsed.fullText) {
          fullText = parsed.fullText.trim();
        }
        
        // If we successfully parsed JSON with headline/byline, return it
        if (headline || byline) {
          console.log('✅ [AiInsightCard] Parsed structured JSON response:', { headline, byline, hasFullText: !!fullText });
          return { headline, byline, fullText };
        }
      }
    } catch (e) {
      // Not JSON or parse failed - fall through to markdown parsing
      console.log('📝 [AiInsightCard] Response is not JSON, falling back to markdown parsing');
    }
    
    // Fallback: Parse as markdown (original behavior)
    const lines = responseText.split('\n').filter(line => line.trim());
    
    // First, try to extract from new format: "Headline: ${headline:headline}" and "Byline: ${byline:byline}"
    const headlineLabelMatch = responseText.match(/^Headline:\s*\$\{headline:headline\}\s*$/m);
    const bylineLabelMatch = responseText.match(/^Byline:\s*\$\{byline:byline\}\s*$/m);
    
    // If we find the new format labels but no entityCards were provided, we can't extract the actual values
    // This is just a marker that the format is correct - actual extraction should come from entityCards
    // But we'll still try to extract from markdown patterns below
    
    // Look for ## or # heading as headline
    const headlineMatch = responseText.match(/^#{1,2}\s+(.+)$/m);
    if (headlineMatch) {
      headline = headlineMatch[1].trim();
    }
    
    // Also try to extract headline from "Headline: [text]" format (if entity reference wasn't replaced)
    if (!headline) {
      const headlineTextMatch = responseText.match(/^Headline:\s*(.+)$/m);
      if (headlineTextMatch && !headlineTextMatch[1].includes('${headline:headline}')) {
        headline = headlineTextMatch[1].trim();
      }
    }
    
    // First paragraph after headline is byline
    const paragraphs = responseText
      .replace(/^#{1,2}\s+.+$/gm, '') // Remove headings
      .replace(/^Headline:\s*.+$/gm, '') // Remove headline label lines
      .replace(/^Byline:\s*.+$/gm, '') // Remove byline label lines
      .split(/\n\n+/)
      .map(p => p.trim())
      .filter(p => p && !p.startsWith('#') && !p.startsWith('Headline:') && !p.startsWith('Byline:'));
    
    if (paragraphs.length > 0) {
      byline = paragraphs[0];
    }
    
    // Also try to extract byline from "Byline: [text]" format (if entity reference wasn't replaced)
    if (!byline) {
      const bylineTextMatch = responseText.match(/^Byline:\s*(.+)$/m);
      if (bylineTextMatch && !bylineTextMatch[1].includes('${byline:byline}')) {
        byline = bylineTextMatch[1].trim();
      }
    }
    
    if (paragraphs.length > 1) {
      fullText = paragraphs.slice(1).join('\n\n');
    }
    
    return { headline, byline, fullText };
  };

  // Render markdown content
  const renderMarkdown = (text: string) => {
    // Basic markdown rendering - bold, italic, links
    let processed = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>');
    
    return <span dangerouslySetInnerHTML={{ __html: processed }} />;
  };

  // Handle "Learn Why" click - just open the main AI chat drawer
  // The ChatInterface already has the messages loaded, we just need to show it
  const handleLearnMoreClick = () => {
    console.log('📊 [AiInsightCard] See Why clicked - opening main AI chat drawer');
    
    // Dispatch the openAIChat event to open the main AI chat drawer in the header
    // This uses the SAME ChatInterface that already has the correct content loaded
    window.dispatchEvent(new CustomEvent('openAIChat', { 
      detail: { 
        threadId: dashboardAi?.lastInsightResponse?.threadId,
        source: 'insight-card' 
      } 
    }));
  };

  // Helper to determine if background is dark
  const isDarkBackground = useMemo(() => {
    if (!safeConfig.backgroundColor) return false;
    const bg = safeConfig.backgroundColor.toLowerCase().trim();
    
    // Check for common dark colors
    if (bg === '#000000' || bg === 'black' || bg === '#000') return true;
    
    // Parse hex color and calculate brightness
    const hexMatch = bg.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (hexMatch) {
      const r = parseInt(hexMatch[1], 16);
      const g = parseInt(hexMatch[2], 16);
      const b = parseInt(hexMatch[3], 16);
      // Calculate relative luminance (0-255 scale, <128 is dark)
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness < 128;
    }
    
    return false;
  }, [safeConfig.backgroundColor]);

  // Get variant-specific styles
  const getVariantStyles = () => {
    // Use backgroundColor from config if provided, otherwise use theme-based default
    const backgroundColor = safeConfig.backgroundColor || 
      (theme.palette.mode === 'dark' ? '#1a1a2e' : '#ffffff');
    
    const baseStyles = {
      backgroundColor,
      color: isDarkBackground ? '#ffffff' : 'inherit',
      borderRadius: '12px',
      border: `1px solid ${safeConfig.accentColor || (theme.palette.mode === 'dark' ? 'rgba(255, 165, 0, 0.3)' : 'rgba(255, 165, 0, 0.4)')}`,
      position: 'relative' as const,
      overflow: 'hidden',
      zIndex: 1, // Ensure card is above adjacent components
    };
    
    switch (safeConfig.variant) {
      case 'compact':
        return {
          ...baseStyles,
          padding: '12px 16px',
          minHeight: '130px', // Increased to accommodate headline/byline without cutoff
        };
      case 'standard':
        return {
          ...baseStyles,
          padding: '16px 20px',
          minHeight: '170px', // Increased to accommodate headline/byline without cutoff
        };
      case 'featured':
      default:
        return {
          ...baseStyles,
          padding: '20px 24px',
          minHeight: '210px', // Increased to accommodate headline/byline without cutoff
        };
    }
  };

  // Always show "Coming Soon" teaser card - AI Insights not yet available
  // This replaces loading, error, and data states - always show teaser regardless of state
  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: '100%',
        flex: '1 1 100%',
        backgroundColor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#ffffff',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 165, 0, 0.3)' : 'rgba(255, 165, 0, 0.4)'}`,
        padding: '10px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        pointerEvents: 'none',
        userSelect: 'none',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
        // Align left edge with KPI cards (8px padding) - remove negative margins
        marginLeft: 0,
        marginRight: 0,
        // Subtle gradient overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '60px',
          height: '60px',
          background: `radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.1)' : 'rgba(249, 115, 22, 0.08)'} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }} 
      className={className}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            borderRadius: '6px',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(249, 115, 22, 0.1))'
              : 'linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(249, 115, 22, 0.08))',
            flexShrink: 0,
          }}
        >
          <SparklesIcon 
            sx={{ 
              fontSize: '12px',
              color: '#f97316',
              animation: 'sparkle 2s ease-in-out infinite',
              '@keyframes sparkle': {
                '0%, 100%': { opacity: 0.8, transform: 'scale(1)' },
                '50%': { opacity: 1, transform: 'scale(1.1)' },
              },
            }} 
          />
        </Box>
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '18px',
            color: theme.palette.mode === 'dark' ? '#ffffff' : '#18181b',
          }}
        >
          AI Insights (Coming Soon)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', pl: '28px' }}>
        <LightbulbIcon 
          sx={{ 
            fontSize: '14px',
            color: theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.6)' : '#f97316',
            mt: '1px',
            flexShrink: 0,
            opacity: 0.7,
          }} 
        />
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#71717a',
            flex: 1,
          }}
        >
          We're building an AI assistant that will help you understand trends across locations and get plain-English explanations of your data.
        </Typography>
      </Box>
    </Box>
  );
};

export default AiInsightCard;
